import { useState, useEffect, useCallback } from 'react';
import { Plus, Printer, Search, X, Loader2, ShieldAlert, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

const FILIAIS = [
  { ordem: 1, nome: 'Loja' },
  { ordem: 6, nome: 'Depósito' },
];

const nomeFilial = (ordem) => FILIAIS.find(f => f.ordem === Number(ordem))?.nome || `Filial ${ordem}`;

function formatarDataHora(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function imprimirFolha(rodada) {
  const dataCorte = formatarDataHora(rodada.data_hora_corte);
  const metodoLabel = rodada.metodo_selecao === 'curva_abc'
    ? `Curva ABC — Classe ${rodada.classe_abc}`
    : 'Seleção manual';

  const linhas = rodada.itens.map((item, idx) => `
    <tr>
      <td>${idx + 1}</td>
      <td>${item.codigo_produto || ''}</td>
      <td>${item.nome_produto || ''}</td>
      <td>${item.codigo_barras || ''}</td>
      <td class="qtd"></td>
      <td class="obs"></td>
    </tr>
  `).join('');

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Folha de Contagem — Rodada ${rodada.id}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:16px}
h1{font-size:16px;font-weight:700;margin-bottom:4px}
.info{display:grid;grid-template-columns:repeat(2,1fr);gap:4px 24px;margin:10px 0 14px;font-size:11px}
.info div{border-bottom:1px solid #e5e7eb;padding-bottom:2px}
.info b{display:inline-block;min-width:130px}
table{width:100%;border-collapse:collapse;margin-top:8px}
th,td{border:1px solid #d1d5db;padding:4px 6px;font-size:10px;text-align:left}
th{background:#f3f4f6}
.qtd{width:70px}
.obs{width:140px}
.rodape{margin-top:16px;font-size:10px}
.rodape .linhas{border:1px solid #d1d5db;height:60px;margin-top:4px}
.assinaturas{display:flex;gap:40px;margin-top:36px}
.assinaturas div{flex:1;text-align:center;border-top:1px solid #111;padding-top:4px;font-size:10px}
@media print{body{padding:10mm}}
</style>
</head>
<body>
<h1>Folha de Contagem — Inventário Rotativo</h1>
<div class="info">
  <div><b>Rodada nº:</b> ${rodada.id}</div>
  <div><b>Filial:</b> ${nomeFilial(rodada.ordem_filial)}</div>
  <div><b>Setor/Descrição:</b> ${rodada.descricao}</div>
  <div><b>Método:</b> ${metodoLabel}</div>
  <div><b>Data e hora do corte:</b> ${dataCorte}</div>
  <div><b>Início / Término:</b> ____________ / ____________</div>
  <div><b>Contador:</b> ____________________________</div>
  <div><b>Registrador:</b> ____________________________</div>
</div>
<table>
  <thead>
    <tr><th>Seq.</th><th>Código</th><th>Descrição</th><th>Cód. Barras</th><th>Qtd. contada</th><th>Observação</th></tr>
  </thead>
  <tbody>${linhas}</tbody>
</table>
<div class="rodape">
  <b>Produtos encontrados no setor e ausentes da lista:</b>
  <div class="linhas"></div>
</div>
<div class="assinaturas">
  <div>Assinatura do Contador</div>
  <div>Assinatura do Registrador</div>
</div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=860,height=700');
  win.document.write(html);
  win.document.close();
  win.onload = () => win.print();
}

// ============================================
// TOGGLE — mesmo padrão inline usado em CobrancaPix.jsx
// ============================================
function ToggleStatus({ contado, onToggle, disabled }) {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      role="switch"
      aria-checked={contado}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${
        contado ? 'bg-green-600' : 'bg-gray-300'
      }`}
    >
      <span
        aria-hidden="true"
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          contado ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// ============================================
// MODAL — Nova rodada
// ============================================
function ModalNovaRodada({ aberto, onClose, onCriada }) {
  const [descricao, setDescricao] = useState('');
  const [ordemFilial, setOrdemFilial] = useState(1);
  const [metodo, setMetodo] = useState('manual');
  const [salvando, setSalvando] = useState(false);

  const [buscaTermo, setBuscaTermo] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [itensSelecionados, setItensSelecionados] = useState([]);

  const [classeAbc, setClasseAbc] = useState('A');
  const [mesesPeriodo, setMesesPeriodo] = useState(12);
  const [calculandoPreview, setCalculandoPreview] = useState(false);
  const [previewItens, setPreviewItens] = useState(null);

  const [excluirContados, setExcluirContados] = useState(false);
  const [mesesExclusao, setMesesExclusao] = useState(2);

  const resetForm = () => {
    setDescricao('');
    setOrdemFilial(1);
    setMetodo('manual');
    setBuscaTermo('');
    setResultadosBusca([]);
    setItensSelecionados([]);
    setClasseAbc('A');
    setMesesPeriodo(12);
    setPreviewItens(null);
    setExcluirContados(false);
    setMesesExclusao(2);
  };

  const handleClose = () => {
    if (salvando) return;
    resetForm();
    onClose();
  };

  async function handleBuscar() {
    if (!buscaTermo.trim()) return;
    setBuscando(true);
    try {
      const resp = await apiService.inventarioRotativo.buscarProdutos(
        buscaTermo.trim(),
        excluirContados ? { excluirContadosMeses: mesesExclusao } : {}
      );
      setResultadosBusca(resp.data.data ?? []);
    } catch {
      toast.error('Erro ao buscar produtos.');
    } finally {
      setBuscando(false);
    }
  }

  function adicionarItem(produto) {
    setItensSelecionados(prev => {
      if (prev.some(p => p.ordemProdServ === produto.Ordem)) return prev;
      return [...prev, {
        ordemProdServ: produto.Ordem,
        codigoProduto: produto.Codigo,
        nomeProduto: produto.Nome,
        codigoBarras: produto.Codigo_Barras,
      }];
    });
  }

  function removerItem(ordemProdServ) {
    setItensSelecionados(prev => prev.filter(p => p.ordemProdServ !== ordemProdServ));
  }

  async function handleCalcularPreview() {
    setCalculandoPreview(true);
    setPreviewItens(null);
    try {
      const dataFim = new Date();
      const dataInicio = new Date();
      dataInicio.setMonth(dataInicio.getMonth() - Number(mesesPeriodo));

      const resp = await apiService.inventarioRotativo.previewCurvaAbc({
        ordemFilial, classe: classeAbc,
        dataInicio: dataInicio.toISOString(), dataFim: dataFim.toISOString(),
        ...(excluirContados ? { excluirContadosMeses: mesesExclusao } : {}),
      });
      setPreviewItens(resp.data.data ?? []);
    } catch {
      toast.error('Erro ao calcular prévia da curva ABC.');
    } finally {
      setCalculandoPreview(false);
    }
  }

  function removerDoPreview(ordemProdServ) {
    setPreviewItens(prev => prev.filter(p => p.ordemProdServ !== ordemProdServ));
  }

  async function handleConfirmar() {
    if (!descricao.trim()) {
      toast.error('Informe a descrição/setor da rodada.');
      return;
    }

    let itens;
    if (metodo === 'manual') {
      itens = itensSelecionados;
    } else {
      itens = (previewItens || []).map(p => ({
        ordemProdServ: p.ordemProdServ, codigoProduto: p.codigoProduto,
        nomeProduto: p.nomeProduto, codigoBarras: p.codigoBarras,
      }));
    }

    if (itens.length === 0) {
      toast.error('A rodada precisa de ao menos 1 item.');
      return;
    }

    setSalvando(true);
    try {
      const resp = await apiService.inventarioRotativo.criar({
        descricao: descricao.trim(),
        ordemFilial,
        metodoSelecao: metodo,
        classeAbc: metodo === 'curva_abc' ? classeAbc : null,
        itens,
      });
      toast.success('Rodada criada com sucesso!');
      resetForm();
      onCriada(resp.data.data);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao criar rodada.');
    } finally {
      setSalvando(false);
    }
  }

  if (!aberto) return null;

  const totalItens = metodo === 'manual' ? itensSelecionados.length : (previewItens?.length ?? 0);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Nova rodada de contagem</h3>
          <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" disabled={salvando}>
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Descrição / Setor *</label>
              <input
                type="text" value={descricao} onChange={(e) => setDescricao(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Ex.: Gôndola A, Eletroportáteis..." disabled={salvando}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filial *</label>
              <select
                value={ordemFilial} onChange={(e) => setOrdemFilial(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={salvando}
              >
                {FILIAIS.map(f => <option key={f.ordem} value={f.ordem}>{f.nome}</option>)}
              </select>
            </div>
          </div>

          <label className="flex items-center gap-3 px-3 py-2 border border-gray-200 rounded-lg bg-gray-50">
            <input
              type="checkbox" checked={excluirContados}
              onChange={(e) => setExcluirContados(e.target.checked)}
              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500"
              disabled={salvando}
            />
            <span className="text-sm text-gray-700 flex-1">
              Remover produtos contados nos últimos
              <select
                value={mesesExclusao}
                onChange={(e) => setMesesExclusao(Number(e.target.value))}
                onClick={(e) => e.stopPropagation()}
                className="mx-1.5 px-2 py-0.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={salvando || !excluirContados}
              >
                <option value={1}>1</option>
                <option value={2}>2</option>
              </select>
              mês(es)
            </span>
          </label>

          <div className="flex gap-2">
            <button
              onClick={() => setMetodo('manual')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border ${metodo === 'manual' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
              disabled={salvando}
            >
              Busca manual
            </button>
            <button
              onClick={() => setMetodo('curva_abc')}
              className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium border ${metodo === 'curva_abc' ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-700 border-gray-300'}`}
              disabled={salvando}
            >
              Curva ABC
            </button>
          </div>

          {metodo === 'manual' ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text" value={buscaTermo} onChange={(e) => setBuscaTermo(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleBuscar(); }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Nome, código ou código de barras" disabled={salvando}
                />
                <button
                  onClick={handleBuscar}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium flex items-center gap-1"
                  disabled={buscando || salvando}
                >
                  {buscando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Buscar
                </button>
              </div>

              {resultadosBusca.length > 0 && (
                <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto divide-y">
                  {resultadosBusca.map(p => (
                    <button
                      key={p.Ordem} onClick={() => adicionarItem(p)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 text-sm flex items-center justify-between"
                    >
                      <span>{p.Nome} <span className="text-gray-400">— {p.Codigo}</span></span>
                      <Plus className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Itens selecionados ({itensSelecionados.length})</p>
                <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto divide-y">
                  {itensSelecionados.length === 0 && (
                    <p className="px-3 py-4 text-sm text-gray-400 text-center">Nenhum item adicionado ainda</p>
                  )}
                  {itensSelecionados.map(item => (
                    <div key={item.ordemProdServ} className="flex items-center justify-between px-3 py-2 text-sm">
                      <span>{item.nomeProduto} <span className="text-gray-400">— {item.codigoProduto}</span></span>
                      <button onClick={() => removerItem(item.ordemProdServ)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Classe</label>
                  <select
                    value={classeAbc} onChange={(e) => setClasseAbc(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={salvando}
                  >
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Período de análise (meses)</label>
                  <input
                    type="number" min={1} max={24} value={mesesPeriodo}
                    onChange={(e) => setMesesPeriodo(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    disabled={salvando}
                  />
                </div>
              </div>
              <button
                onClick={handleCalcularPreview}
                className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                disabled={calculandoPreview || salvando}
              >
                {calculandoPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Calcular prévia
              </button>

              {previewItens !== null && (
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Itens na classe {classeAbc} ({previewItens.length})</p>
                  <div className="border border-gray-200 rounded-lg max-h-56 overflow-y-auto divide-y">
                    {previewItens.length === 0 && (
                      <p className="px-3 py-4 text-sm text-gray-400 text-center">Nenhum produto encontrado nesse período/classe.</p>
                    )}
                    {previewItens.map(item => (
                      <div key={item.ordemProdServ} className="flex items-center justify-between px-3 py-2 text-sm">
                        <span>{item.nomeProduto} <span className="text-gray-400">— {item.codigoProduto}</span></span>
                        <button onClick={() => removerDoPreview(item.ordemProdServ)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">{totalItens} item(ns) na rodada</p>
          <div className="flex gap-3">
            <button onClick={handleClose} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium" disabled={salvando}>
              Cancelar
            </button>
            <button
              onClick={handleConfirmar}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:bg-gray-300"
              disabled={salvando || totalItens === 0}
            >
              {salvando ? 'Gerando...' : 'Gerar rodada'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PÁGINA PRINCIPAL
// ============================================
export default function InventarioRotativo() {
  const { isGerenteOuAdmin } = useAuth();
  const permitido = isGerenteOuAdmin();

  const [rodadas, setRodadas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalAberto, setModalAberto] = useState(false);
  const [alternandoId, setAlternandoId] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiService.inventarioRotativo.listar();
      setRodadas(resp.data.data ?? []);
    } catch {
      toast.error('Erro ao carregar rodadas de inventário rotativo.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (permitido) carregar();
  }, [permitido, carregar]);

  async function handleImprimir(rodada) {
    try {
      const resp = await apiService.inventarioRotativo.detalhe(rodada.id);
      imprimirFolha(resp.data.data);
    } catch {
      toast.error('Erro ao carregar folha de contagem.');
    }
  }

  async function handleToggleStatus(rodada) {
    setAlternandoId(rodada.id);
    try {
      const resp = await apiService.inventarioRotativo.atualizarStatus(rodada.id, rodada.status !== 'contado');
      setRodadas(prev => prev.map(r => (r.id === rodada.id ? { ...r, ...resp.data.data } : r)));
    } catch {
      toast.error('Erro ao atualizar status da rodada.');
    } finally {
      setAlternandoId(null);
    }
  }

  function handleRodadaCriada(rodada) {
    setModalAberto(false);
    carregar();
    imprimirFolha(rodada);
  }

  const existePendente = rodadas.some(r => r.status !== 'contado');

  if (!permitido) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Acesso restrito</h2>
          <p className="text-gray-600">Esta página é restrita a gerentes e administradores.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-lg shadow-lg p-6 text-white flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Inventário Rotativo</h1>
          <p className="text-indigo-100 text-sm mt-1">Rodadas de contagem cíclica — geração e impressão de folhas de contagem (POP-EST-001)</p>
        </div>
        <button
          onClick={() => setModalAberto(true)}
          disabled={existePendente}
          title={existePendente ? 'Marque todas as rodadas pendentes como "Contado" antes de gerar uma nova.' : undefined}
          className="px-4 py-2 bg-white text-indigo-700 rounded-lg font-medium flex items-center gap-2 hover:bg-indigo-50 disabled:bg-white/50 disabled:text-indigo-300 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" /> Nova rodada
        </button>
      </div>

      {existePendente && !loading && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3">
          Há rodada(s) pendente(s) de contagem. Marque todas como "Contado" para poder gerar uma nova rodada.
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Nº</th>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Descrição / Setor</th>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Filial</th>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Método</th>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Data/hora de geração</th>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Itens</th>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Contado</th>
              <th className="text-left px-4 py-3 font-medium text-xs text-gray-500 uppercase">Imprimir</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> Carregando...
              </td></tr>
            )}
            {!loading && rodadas.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Nenhuma rodada criada ainda.</td></tr>
            )}
            {!loading && rodadas.map(rodada => (
              <tr key={rodada.id} className="text-sm">
                <td className="px-4 py-3 font-medium text-gray-700">#{rodada.id}</td>
                <td className="px-4 py-3">{rodada.descricao}</td>
                <td className="px-4 py-3">{nomeFilial(rodada.ordem_filial)}</td>
                <td className="px-4 py-3">
                  {rodada.metodo_selecao === 'curva_abc' ? `Curva ABC (${rodada.classe_abc})` : 'Manual'}
                </td>
                <td className="px-4 py-3 text-gray-500">{formatarDataHora(rodada.data_hora_corte)}</td>
                <td className="px-4 py-3">{rodada.total_itens}</td>
                <td className="px-4 py-3">
                  <ToggleStatus
                    contado={rodada.status === 'contado'}
                    onToggle={() => handleToggleStatus(rodada)}
                    disabled={alternandoId === rodada.id}
                  />
                </td>
                <td className="px-4 py-3">
                  <button
                    onClick={() => handleImprimir(rodada)}
                    className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg"
                    title="Imprimir folha de contagem"
                  >
                    <Printer className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ModalNovaRodada
        aberto={modalAberto}
        onClose={() => setModalAberto(false)}
        onCriada={handleRodadaCriada}
      />
    </div>
  );
}
